import React, { useState, useMemo } from 'react';
import { Check, ChevronDown, Search, X } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Button } from '../ui/button';
import { Input } from '../ui/input';

/**
 * Selector con búsqueda en vivo de ejercicios.
 * Filtra por nombre + GF-ID + alias (insensible a tildes/mayúsculas).
 *
 * Props:
 *   - exercises: [{ id, name, gf_id?, official_id?, primary_muscle? }, ...]
 *   - value: string (id del ejercicio actualmente seleccionado)
 *   - onChange: (id, exercise) => void
 *   - excludeIds: string[] (no mostrar estos IDs)
 *   - placeholder: string
 *   - accent: 'purple' | 'amber' (color del foco/check)
 *   - className?: extra clases para el trigger
 */
const norm = (s) =>
  (s || '')
    .toString()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();

const ExerciseSearchSelect = ({
  exercises = [],
  value,
  onChange,
  excludeIds = [],
  placeholder = 'Buscar y seleccionar...',
  accent = 'purple',
  className = '',
  testId = 'exercise-search-select',
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');

  const excludeSet = useMemo(() => new Set(excludeIds.filter(Boolean)), [excludeIds]);
  const selected = useMemo(
    () => exercises.find((e) => e.id === value) || null,
    [exercises, value]
  );

  const filtered = useMemo(() => {
    const q = norm(query);
    const base = exercises.filter((e) => !excludeSet.has(e.id));
    if (!q) return base; // sin query: TODOS los ejercicios disponibles
    const tokens = q.split(/\s+/).filter(Boolean);
    return base.filter((e) => {
      const hay = norm(
        [e.name, e.gf_id, e.official_id, e.primary_muscle, ...(e.tags || [])].join(' ')
      );
      return tokens.every((t) => hay.includes(t));
    });
  }, [exercises, excludeSet, query]);

  const accentClasses =
    accent === 'amber'
      ? {
          ring: 'focus-within:ring-amber-500/50',
          border: 'border-amber-500/30',
          check: 'text-amber-400',
          hover: 'hover:bg-amber-500/10',
        }
      : {
          ring: 'focus-within:ring-purple-500/50',
          border: 'border-purple-500/30',
          check: 'text-purple-400',
          hover: 'hover:bg-purple-500/10',
        };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          data-testid={testId}
          className={`w-full justify-between bg-zinc-900/50 ${accentClasses.border} h-9 text-left font-normal ${className}`}
        >
          <span className={selected ? 'text-foreground truncate' : 'text-muted-foreground'}>
            {selected ? selected.name : placeholder}
          </span>
          <ChevronDown className="h-4 w-4 opacity-50 flex-shrink-0 ml-2" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[var(--radix-popover-trigger-width)] p-0 bg-zinc-900 border-zinc-700"
        align="start"
      >
        <div className={`flex items-center border-b border-zinc-800 px-2 ${accentClasses.ring}`}>
          <Search className="h-4 w-4 mr-2 text-muted-foreground flex-shrink-0" />
          <Input
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Escribe: press, remo, GF-001..."
            className="h-9 border-0 bg-transparent focus-visible:ring-0 px-0"
            data-testid={`${testId}-input`}
          />
          {query && (
            <button
              type="button"
              onClick={() => setQuery('')}
              className="text-muted-foreground hover:text-foreground"
              aria-label="Limpiar"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
        <div className="max-h-72 overflow-y-auto py-1">
          {filtered.length === 0 ? (
            <div className="text-center text-sm text-muted-foreground py-6 px-3">
              Sin resultados para "{query}"
            </div>
          ) : (
            filtered.map((ex) => {
              const isSel = ex.id === value;
              return (
                <button
                  type="button"
                  key={ex.id}
                  data-testid={`${testId}-opt-${ex.id}`}
                  onClick={() => {
                    onChange(ex.id, ex);
                    setOpen(false);
                    setQuery('');
                  }}
                  className={`w-full text-left px-3 py-2 text-sm flex items-center gap-2 ${accentClasses.hover} ${
                    isSel ? 'bg-zinc-800' : ''
                  }`}
                >
                  <Check
                    className={`h-3.5 w-3.5 flex-shrink-0 ${
                      isSel ? accentClasses.check : 'opacity-0'
                    }`}
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-foreground truncate">{ex.name}</p>
                    {(ex.gf_id || ex.official_id || ex.primary_muscle) && (
                      <p className="text-[10px] text-muted-foreground truncate">
                        {ex.gf_id || ex.official_id || ''}
                        {ex.primary_muscle ? ` · ${ex.primary_muscle}` : ''}
                      </p>
                    )}
                  </div>
                </button>
              );
            })
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default ExerciseSearchSelect;
